import { useRouter } from "next/router";
import React, { FC, ReactNode, useCallback, useState } from "react";
import { useMount } from "react-use";

import { useFetchCurrentVisitsQuery } from "./document.gen";

import { CheckInConfirmDialog } from "src/components/CheckInConfirmDialog/CheckInConfirmDialog";
import { useCurrentAccount } from "src/entities/account/useCurrentAccount";
import {
  getNotifiedVisitIds,
  setNotifiedVisitIds,
  removeExpiredNotifiedVisitIds,
} from "src/store/localStorage/notifiedVisitIds";

const DIALOG_REJECT_PATH_NAMES = ["/home/downloads/banners", "/banner"];

export const CurrentVisitProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { currentAccount } = useCurrentAccount();
  const [audio, setAudio] = useState<HTMLAudioElement>();
  const router = useRouter();

  useFetchCurrentVisitsQuery(
    currentAccount && !currentAccount.currentClinic.receptionAccessAvailable
      ? {
          pollInterval: 30000,
          fetchPolicy: "no-cache",
          onCompleted: (data) => {
            if (typeof localStorage === "undefined") return [];
            if (!currentAccount.preference.checkInNotificationEnabled) return;

            const notifiedVisitIds = getNotifiedVisitIds({ storage: localStorage });
            const notifiableVisitIds = data.currentVisits
              .filter((visit) => !visit.checkInConfirmedAt && !notifiedVisitIds.includes(visit.id))
              .map((visit) => visit.id);

            if (notifiableVisitIds.length === 0) return;

            setNotifiedVisitIds({ notifiedVisitIds: notifiableVisitIds, storage: localStorage });

            ringCheckInSound();
          },
        }
      : { skip: true },
  );

  const ringCheckInSound = useCallback(() => {
    if (!audio) return;

    audio.play().catch((error) => console.warn("visit notification audio play error", { error }));
  }, [audio]);

  const enableCheckInSound = useCallback(() => {
    if (!audio) return;

    audio.load();
  }, [audio]);

  useMount(() => {
    removeExpiredNotifiedVisitIds({
      storage: typeof localStorage !== "undefined" ? localStorage : undefined,
    });
  });

  useMount(() => setAudio(new Audio("/chime01.wav")));

  return (
    <>
      {currentAccount?.preference.checkInNotificationEnabled &&
        !currentAccount.currentClinic.receptionAccessAvailable &&
        router.isReady &&
        !DIALOG_REJECT_PATH_NAMES.includes(router.pathname) && (
          <CheckInConfirmDialog onCloseDialog={enableCheckInSound} />
        )}

      {children}
    </>
  );
};
